import React, { useCallback, useEffect, useState } from 'react';

import { Skeleton } from '@material-ui/lab';
import { PlayIcon } from 'components/PlayIcon';
import useAsync from 'components/hooks/useAsync';
import styled, { css } from 'styled-components';
import { StyledBox, VerticallyAlignedFlexContainer } from 'styles/common';

import theme from 'constants/theme';

import { getCompetenciesForRole } from 'services/growth/oneOnOne';

import { Typography13 } from 'uiToolkit/Typography';

import SubCompetencyList from '../competencyScreen/SubCompetencyList';

const VerticalLine = styled.div`
  margin-left: 12px;
  height: 100%;
  border-left: 1px solid ${(props) => props.theme.palette.primary.main};
`;

const ExpandIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-right: 16px;
`;

const StyledPlayIcon = styled(PlayIcon)`
  ${(props) =>
    !props.$hasSubCompetencies &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `}
  margin-top: 16px;
  cursor: pointer;
  flex: 0 0 auto;
`;

const RowContainer = styled.div`
  display: flex;
  align-items: stretch;
  // padding: 0rem 1.5rem;
  &:hover {
    background-color: ${(props) => props.theme.palette.background.blue.light.v2};
  }
`;

const RoleSelectionCompetencyRow = ({ name, subCompetencies }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpansion = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, [setIsExpanded]);

  const subCompetenciesArray =
    subCompetencies?.map(({ text, uuid }) => ({
      name: text,
      subCompetencyId: uuid,
    })) ?? null;

  const subCompetenciesString = subCompetenciesArray?.map(({ name }) => name)?.join(', ') ?? '';
  const hasSubCompetencies = Boolean(subCompetencies?.length);

  return (
    <>
      <RowContainer>
        <ExpandIconContainer>
          <StyledPlayIcon
            fillColor={hasSubCompetencies ? theme.palette.primary.main : theme.palette.grey.main}
            backgroundColor={hasSubCompetencies ? theme.palette.primary.main : theme.palette.grey.main}
            onClick={toggleExpansion}
            $hasSubCompetencies={subCompetencies?.length}
          />
          {isExpanded ? <VerticalLine /> : null}
        </ExpandIconContainer>
        <StyledBox width="100%" topGutter={2} bottomGutter={2}>
          <VerticallyAlignedFlexContainer>
            <Typography13 weight={600} color={theme.palette.grey.dark}>
              {name}
            </Typography13>
            {!isExpanded && (
              <div
                style={{
                  marginLeft: 'auto',
                  maxWidth: '25ch',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <Typography13 color={theme.palette.text.primary}>{subCompetenciesString}</Typography13>
              </div>
            )}
          </VerticallyAlignedFlexContainer>
        </StyledBox>
      </RowContainer>
      {isExpanded ? <SubCompetencyList paddingLeft="12px" subCompetencies={subCompetenciesArray} /> : null}
    </>
  );
};

const RoleSelectionCompetencyPreview = ({ functionId, roleId }) => {
  const { run, isLoading, isIdle } = useAsync();

  const [competencies, setCompetencies] = useState([]);

  useEffect(() => {
    if (functionId && roleId) {
      run(
        getCompetenciesForRole({ functionId, roleId }).then((res) => {
          setCompetencies(res);
        })
      );
    }
  }, [functionId, roleId]);

  if (isLoading) {
    return <Skeleton />;
  }

  return (
    <div>
      <Typography13 bottomGutter={2} topGutter={2} weight={600}>
        Competency Preview
      </Typography13>
      {competencies?.map(({ name, subCompetencies }) => (
        <RoleSelectionCompetencyRow name={name} subCompetencies={subCompetencies} />
      ))}
    </div>
  );
};

export default RoleSelectionCompetencyPreview;
